import { addDays, addMonths, differenceInMonths } from "date-fns";
import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import LovDelimiter from "src/components/Lov/Delimiter";
import Modal from "src/components/Modal";
import { ModalCenter } from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import downloadFile from "src/utils/downloadFile";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { IModalExportDelimitedSctructure, IOpenModalExportDelimitedStructure, IRefModalExportDelimitedSctructure } from "./interface";
import schemaExportStructure from "./schemas";

const ModalExportDelimitedSctructure: React.ForwardRefRenderFunction<IRefModalExportDelimitedSctructure, IModalExportDelimitedSctructure> = ({
	origin
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal export delimited structure
	const modalDelimitedStructure = useRef<RCCRefModalProps>(null);
	const [isDelimited, setIsDelimited] = useState(false);
	const [subtitleModal, setSubtitleModal] = useState<string | undefined>(undefined);
	const [memorizedParams, setMemorizedParams] = useState<IOpenModalExportDelimitedStructure>({period: String(new Date().getFullYear()) } as IOpenModalExportDelimitedStructure);
	const [startDate, setStartDate] = useState(new Date());
	const {
		setValue: setValueDelimitedScructure,
		control: controlDelimitedScructure, 
		register: registerDelimitedScructure, 
		handleSubmit: handleSubmitDelimitedScructure,
		formState: { errors: errorsDelimitedScructure },
		clearErrors: clearErrorsDelimitedScructure,
		setError: setErrorDelimitedScructure,
		reset: resetDelimitedStructure
	} = useForm();
	const openModalExportStructure  = async ({ ofa001Id, budgetId, subtitle, subsidiary, period, startMonth }: IOpenModalExportDelimitedStructure) => {
		resetDelimitedStructure();
		setMemorizedParams({
			subsidiary,
			budgetId,
			ofa001Id,
			period,
			subtitle,
			startMonth
		});
		const newDate = new Date(Number(period), startMonth - 1, 1);		
		setIsDelimited(false);
		setStartDate(newDate);
		setSubtitleModal(subtitle);
		modalDelimitedStructure.current?.openModal();
	};
	const openModalExportDelimitedStructure = async ({ ofa001Id, budgetId, subtitle, subsidiary, period, startMonth }: IOpenModalExportDelimitedStructure) => {
		resetDelimitedStructure();
		setMemorizedParams({
			subsidiary,
			budgetId,
			ofa001Id,
			period,
			subtitle,
			startMonth
		});
		const newDate = new Date(Number(period), startMonth - 1, 1);		
		setIsDelimited(true);
		setStartDate(newDate);
		setSubtitleModal(subtitle);
		modalDelimitedStructure.current?.openModal();
	};
	const submitDelimitedScructure = async (data: any) => {
		try{
			clearErrorsDelimitedScructure();
		
			await schemaExportStructure(translate).validate({...data}, {
				abortEarly: false
			});

			const months = differenceInMonths(data.endDate, data.startDate);

			if(months >= 12){
				addToast({
					color: "yellow",
					description: translate("Não pode exportar um período maior que um ano"),
					title: translate("Atenção")
				});
				return;
			}

			modalDelimitedStructure.current?.setLoadingButton();
			const response = await RCCServicesApi.getRequest({
				method: isDelimited ? "FinancialManagement/ExportDelimitedStructure" : "FinancialManagement/ExportStructure",
				responseType:  isDelimited ? undefined : "blob",
				contentType: isDelimited ? undefined : "application/x-www-form-urlencoded",
				urlParameters: {
					budgetPlanId: memorizedParams.ofa001Id,
					budgetId: memorizedParams.budgetId,
					subsidiary: memorizedParams.subsidiary.value,
					origin,
					...data,
					startDate: formatDate(data.startDate),
					endDate: formatDate(data.endDate),
					title: `${subtitleModal} - ${formatDate(data.startDate)} - ${formatDate(data.endDate)}`
				}
			});

			downloadFile(response.data, `${subtitleModal} - ${formatDate(data.startDate)} - ${formatDate(data.endDate)}.${isDelimited ? "csv" : "xlsx"}`, isDelimited);

			addToast({
				color: "green",
				description: translate("Estrutura exportada com sucesso"),
				title: translate("Sucesso")
			});

			modalDelimitedStructure.current?.closeModal();
			modalDelimitedStructure.current?.resetLoadingButton();
		}catch(err){
			modalDelimitedStructure.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelimitedScructure)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao exportar estrutura valores"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalExportDelimitedStructure,
		openModalExportStructure
	}));

	return (
		<>
			<Modal 
				ref={modalDelimitedStructure}
				header={{
					title: isDelimited ? translate("Exportar estrutura delimitada") : translate("Exportar estrutura"),
					subtitle: subtitleModal,
					icon: {
						name: "download"
					}
				}}
				styleInline={`${ModalCenter} width: 55vw; height: 400px;`}
				onSubmit={handleSubmitDelimitedScructure(submitDelimitedScructure)}
			>
				<RCCGrid container item spacing={1} justifyContent="center">
					<RCCGrid container item xs={12} sm={10}>
						{isDelimited ? (
							<>
								<RCCGrid container item xs={6}>
									<RCCSwitch
										name="exportOnlyWithValues"
										register={registerDelimitedScructure}
										label={translate("Exportar apenas linhas com valores")}
										defaultChecked
									/>
								</RCCGrid>
								<RCCGrid container item xs={6}>
									<LovDelimiter
										control={controlDelimitedScructure}
										name="delimiter"
										label={translate("Delimitador")}
									/>
								</RCCGrid>
							</>
						) : (
							<RCCGrid container item>
								<RCCSwitch
									name="exportValues"
									register={registerDelimitedScructure}
									label={translate("Exportar valores")}
									defaultChecked
								/>
							</RCCGrid>
						)}
					</RCCGrid>
					<RCCGrid container item xs={12} sm={10}>
						<DateTimePickerRange
							control={controlDelimitedScructure}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit = "startDate"
							nameEnd = "endDate"
							defaultValueEnd={addDays(addMonths(startDate, 12), -1)}
							defaultValueInit={startDate}
							type="datePicker"
							setValue={setValueDelimitedScructure}
							error={errorsDelimitedScructure.startDate || errorsDelimitedScructure.endDate}
							props={{
								datePicker: {
									format: "MM/yyyy",
									views: ["year", "month"]
								}
							}}
						/> 
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalExportDelimitedSctructure);
